import type { ModificationStatus } from '@mai-team-amusement/core';
import { ApiMethod, apiRequest } from '@mai-team-amusement/ui';
import {
  ClockApiPath,
  apiPath,
  zAuditPage,
  zClockStatus,
  zGeofenceList,
  zGeofenceView,
  zGroupList,
  zModificationList,
  zModificationView,
  zMonthRecords,
  zPendingCount,
  zScheduleList,
  zScheduleView,
  zTeamMonth,
  zTeamToday,
  zWorkDayDetail,
  type GeofenceInput,
  type GeofenceUpdate,
  type ModificationInput,
  type ModificationScope,
  type PunchInput,
  type ScheduleInput,
} from '@mai-work-clock/contracts';
import { z } from 'zod';

const zNoContent = z.undefined();

function withQuery(path: string, params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      search.set(key, String(value));
    }
  }
  const text = search.toString();
  return text === '' ? path : `${path}?${text}`;
}

export const CsvKind = {
  Monthly: 'monthly',
  Annual: 'annual',
} as const;

export type CsvKind = (typeof CsvKind)[keyof typeof CsvKind];

export const clockApi = {
  status: () => apiRequest(ClockApiPath.Clock, zClockStatus),
  punch: (input: PunchInput) =>
    apiRequest(ClockApiPath.Punches, zClockStatus, { method: ApiMethod.Post, body: input }),

  records: (sub: string, month: string) =>
    apiRequest(withQuery(apiPath(ClockApiPath.Records, { sub }), { month }), zMonthRecords),
  day: (sub: string, date: string) =>
    apiRequest(apiPath(ClockApiPath.WorkDay, { sub, date }), zWorkDayDetail),
  /** Same-origin download link (the session cookie authorizes it). */
  csvUrl: (sub: string, kind: CsvKind, period: string) =>
    kind === CsvKind.Monthly
      ? withQuery(apiPath(ClockApiPath.MonthlyCsv, { sub }), { month: period })
      : withQuery(apiPath(ClockApiPath.AnnualCsv, { sub }), { year: period }),

  requests: (scope: ModificationScope, status?: ModificationStatus) =>
    apiRequest(withQuery(ClockApiPath.Requests, { scope, status }), zModificationList),
  pendingCount: () => apiRequest(ClockApiPath.PendingCount, zPendingCount),
  createRequest: (input: ModificationInput) =>
    apiRequest(ClockApiPath.Requests, zModificationView, { method: ApiMethod.Post, body: input }),
  approve: (id: number, comment: string) =>
    apiRequest(apiPath(ClockApiPath.Approve, { id }), zModificationView, {
      method: ApiMethod.Post,
      body: comment === '' ? {} : { comment },
    }),
  reject: (id: number, comment: string) =>
    apiRequest(apiPath(ClockApiPath.Reject, { id }), zModificationView, {
      method: ApiMethod.Post,
      body: comment === '' ? {} : { comment },
    }),
  cancel: (id: number) =>
    apiRequest(apiPath(ClockApiPath.Cancel, { id }), zModificationView, {
      method: ApiMethod.Post,
    }),

  teamToday: (groupId?: number) =>
    apiRequest(withQuery(ClockApiPath.TeamToday, { groupId }), zTeamToday),
  teamMonth: (month: string, groupId?: number) =>
    apiRequest(withQuery(ClockApiPath.TeamMonth, { month, groupId }), zTeamMonth),
  groups: () => apiRequest(ClockApiPath.Groups, zGroupList),

  schedules: (target: { groupId: number } | { userSub: string }) =>
    apiRequest(
      withQuery(
        ClockApiPath.Schedules,
        'groupId' in target ? { groupId: target.groupId } : { userSub: target.userSub },
      ),
      zScheduleList,
    ),
  saveSchedule: (input: ScheduleInput) =>
    apiRequest(ClockApiPath.Schedules, zScheduleView, { method: ApiMethod.Put, body: input }),
  deleteSchedule: (id: number) =>
    apiRequest(apiPath(ClockApiPath.Schedule, { id }), zNoContent, { method: ApiMethod.Delete }),

  geofences: (groupId?: number) =>
    apiRequest(withQuery(ClockApiPath.Geofences, { groupId }), zGeofenceList),
  createGeofence: (input: GeofenceInput) =>
    apiRequest(ClockApiPath.Geofences, zGeofenceView, { method: ApiMethod.Post, body: input }),
  updateGeofence: (id: number, patch: GeofenceUpdate) =>
    apiRequest(apiPath(ClockApiPath.Geofence, { id }), zGeofenceView, {
      method: ApiMethod.Patch,
      body: patch,
    }),
  deleteGeofence: (id: number) =>
    apiRequest(apiPath(ClockApiPath.Geofence, { id }), zNoContent, { method: ApiMethod.Delete }),

  auditLogs: (page: number, pageSize: number) =>
    apiRequest(withQuery(ClockApiPath.AuditLogs, { page, pageSize }), zAuditPage),
};

/** TanStack Query keys (prefixes invalidate everything below them). */
export const ClockQueryKey = {
  Status: ['clock', 'status'],
  Records: ['clock', 'records'],
  Day: ['clock', 'day'],
  Requests: ['clock', 'requests'],
  PendingCount: ['clock', 'pending-count'],
  Team: ['clock', 'team'],
  Groups: ['clock', 'groups'],
  Schedules: ['clock', 'schedules'],
  Geofences: ['clock', 'geofences'],
  Audit: ['clock', 'audit'],
} as const;
